"use client";

import { useState } from "react";
import Link from "next/link";
import DeliveryTruckIcon from "@/components/icons/DeliveryTruckIcon";
import HeadsetIcon from "@/components/icons/HeadsetIcon";
import CheckIcon from "@/components/icons/CheckIcon";
import MailIcon from "@/components/icons/MailIcon";
import { Button } from "@/components";

type DeliveryMode = "same-day" | "mail-order";

const MODE_COPY: Record<
  DeliveryMode,
  {
    heading: string;
    description: string;
    details: string[];
    ctaLabel: string;
    ctaHref: string;
  }
> = {
  "same-day": {
    heading: "Daily Delivery: 9am\u201311pm",
    description:
      "Order before 11pm for same-day delivery in eligible Calgary-area zones. Late orders arrive next morning at 9am.",
    details: [
      "Rate: $9.99 delivery fee",
      "Min Order: $50 purchase",
      "Free Delivery: $99+",
      "Tracking: Real-time SMS",
    ],
    ctaLabel: "Order Before 3 PM",
    ctaHref: "/sameday-delivery",
  },
  "mail-order": {
    heading: "Cut-off Time: 3 PM MST",
    description:
      "Place your order before 3 PM MST on business days for same-day packing. Alberta-wide Xpresspost with tracking to every postal code.",
    details: [
      "Rate: Flat fee $14.99",
      "Free Ship: On orders $199+",
      "Min Order: $50 purchase",
      "Tracking: Real-time updates",
      "Secure: Signature optional",
    ],
    ctaLabel: "Order Before 3 PM",
    ctaHref: "/search?delivery=mail-order",
  },
};

export default function MailOrderAlbertaServiceSection() {
  const [mode, setMode] = useState<DeliveryMode>("mail-order");
  const content = MODE_COPY[mode];

  return (
    <section className="w-full max-w-none" aria-labelledby="mail-order-alberta-service-heading">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h2
            id="mail-order-alberta-service-heading"
            className="text-h3-mobile sm:text-h3-desktop font-bold text-text-primary"
          >
            Alberta mail order marijuana
          </h2>

          <div className="inline-flex w-full items-center rounded-xl border border-neutral-10 bg-constant-white p-1 lg:w-max gap-1">
            <Button
              variant={ mode === "same-day" ? "primary" : "bordered" }
              size="md"
              onClick={() => setMode("same-day")}
              className="max-sm:w-full rounded-lg px-4 py-2"
            >
              Same-day delivery
            </Button>
            <Button
              variant={ mode === "mail-order" ? "primary" : "bordered" }
              size="md"
              onClick={() => setMode("mail-order")}
              className="max-sm:w-full rounded-lg px-4 py-2"
            >
              Mail order delivery
            </Button>
          </div>
        </div>

        <div className="grid gap-3 lg:grid-cols-[1.5fr_1fr]">
          <article className="overflow-hidden rounded-2xl border border-blue-15 bg-constant-white shadow-blue-10 drop-shadow-lg">
            <div className="flex flex-col lg:flex-row">
              <div className="flex flex-1 flex-col justify-between p-5 sm:p-6">
                <div>
                  <div className="mb-4 flex w-max rounded-xl bg-blue-5 p-4 text-blue-80">
                    {mode === "mail-order" ? <MailIcon size={32} /> : <DeliveryTruckIcon size={32} />}
                  </div>

                  <h3 className="text-[22px] font-bold leading-tight text-blue-60 sm:text-h3-desktop">
                    {content.heading}
                  </h3>
                  <p className="mt-2 text-body-md font-medium leading-relaxed text-text-secondary">
                    {content.description}
                  </p>
                </div>

                <Button asChild variant="primary" size="lg" className="w-full mt-4 rounded-xl sm:w-max px-6 py-3">
                  <Link href={content.ctaHref}>
                    {content.ctaLabel}
                  </Link>
                </Button>
              </div>

              <div className="border-t border-neutral-10 p-5 sm:p-6 lg:border-l lg:border-t-0">
                <ul className="flex flex-col justify-center gap-4 lg:h-full">
                  {content.details.map((detail) => {
                    const sep = detail.indexOf(": ");
                    const label = sep >= 0 ? detail.slice(0, sep) : detail;
                    const value = sep >= 0 ? detail.slice(sep + 2) : "";
                    return (
                      <li key={detail} className="flex items-center gap-2.5 text-body-16-desktop">
                        <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-blue-60 text-constant-white">
                          <CheckIcon size={12} color="white" />
                        </span>
                        <span>
                          <span className="font-bold text-text-primary">{label}:</span>{" "}
                          {value ? (
                            <span className="font-medium text-text-primary">{value}</span>
                          ) : null}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </article>

          <article className="flex flex-col justify-between rounded-2xl border border-neutral-10 bg-constant-white p-5 sm:p-6">
            <div>
              <div className="mb-4 flex w-max rounded-xl bg-blue-5 p-4 text-blue-80">
                <HeadsetIcon size={32} />
              </div>

              <h3 className="text-[22px] font-bold leading-tight text-blue-60 sm:text-h3-desktop">Have questions?</h3>
              <p className="mt-2 text-body-md font-medium leading-relaxed text-text-primary">
                Check our FAQ for instant answers about shipping, tracking, and returns before contacting support
              </p>
            </div>

            <div className="mt-5">
              <Button asChild variant="bordered" color="white" size="md">
                <Link href="/faq">
                  Read FAQ
                </Link>
              </Button>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
